import { useMemo } from 'react';

import { monorepoRoutePaths } from '@apps/admin/route.paths';
import { useDashboardTelemetry } from '@apps/admin/routes/Dashboard/Dashboard.telemetry';
import { useRouterHelper, useTranslation } from '@shared/core';
import { Gift, Shipping, Shop } from '@withjoy/joykit/icons';

import { useFeatureValue } from '@shared/core/featureFlags';
import type { ProductVerticals } from '@shared/core/productVerticals/productVerticals.types';
import type { ProductRow } from '../../PrimaryDashboardContent/PrimaryDashboardContent.types';
import type { PageInfo } from './useProductRows';

export const useRegistryProductRow = (_eventPages: ReadonlyArray<PageInfo>, productVerticals: ProductVerticals, pendingRefundOrdersCount?: number): ProductRow => {
  const routeHelpers = useRouterHelper();
  const telemetry = useDashboardTelemetry();
  const { t2 } = useTranslation('adminDashboardV2');
  const { registry: registryTranslations } = t2('sections');
  const registryTitlesExperimentEnabled = useFeatureValue('registryDashboardTitlesExperiment').value === 'treatment';

  const registryProductRow = useMemo<ProductRow>(() => {
    const registryStrings = registryTranslations;
    const isEnabled = !productVerticals.optOutOfRegistry;

    return {
      title: registryStrings.title,
      isEnabled,
      children: [
        {
          id: 'registry-shop',
          title: registryTitlesExperimentEnabled ? registryStrings.children.shop.experimentTitle : registryStrings.children.shop.title,
          icon: Shop,
          path: routeHelpers.buildPath(monorepoRoutePaths.registryShop),
          pathIsInJoyWeb: true,
          badge: 'New',
          onClick: (categoryArg?: string) => telemetry.openPage({ label: 'shop', section: 'Registry', categoryArg }),
          telemetryName: monorepoRoutePaths.registryShop,
        },
        {
          id: 'registry-manage',
          title: registryStrings.children.manage.title,
          icon: Gift,
          path: routeHelpers.buildPath(monorepoRoutePaths.registryManage),
          pathIsInJoyWeb: true,
          onClick: (categoryArg?: string) => telemetry.openPage({ label: 'manage', section: 'Registry', categoryArg }),
          telemetryName: monorepoRoutePaths.registryManage,
        },
        {
          id: 'registry-track',
          title: registryStrings.children.track.title,
          icon: Shipping,
          path: routeHelpers.buildPath(monorepoRoutePaths.registryTrack),
          pathIsInJoyWeb: true,
          onClick: (categoryArg?: string) => telemetry.openPage({ label: 'track', section: 'Registry', categoryArg }),
          telemetryName: monorepoRoutePaths.registryTrack,
          notificationPill: pendingRefundOrdersCount ? String(pendingRefundOrdersCount) : undefined,
        },
      ],
    };
  }, [registryTranslations, routeHelpers, telemetry, productVerticals, pendingRefundOrdersCount, registryTitlesExperimentEnabled]);

  return registryProductRow;
};
